import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Send Famous Shipment to Orderful 856 Step
 *
 * Fetches a Famous shipment by order number and sends it to Orderful as an 856 ASN
 * (Advanced Shipment Notice) EDI transaction.
 *
 * This step:
 * - Takes a Famous order number as input
 * - Fetches the shipment details from Famous Oracle database
 * - Validates the shipment data and customer information
 * - Builds and sends an 856 EDI message to Orderful
 * - Records the EDI transmission status
 *
 * Note: This is a backend-only step that requires Famous database access and
 * Orderful API integration.
 */
export const sendFamousShipmentToOrderful856Step = defineStepConfig({
  type: "send_famous_shipment_to_orderful_856",
  name: "Send Famous Shipment to Orderful 856",
  description:
    "Fetch a Famous shipment by order number and send it to Orderful as an 856 ASN EDI transaction",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    famousOrderNumber: z
      .string()
      .min(1)
      .describe("The Famous order/shipment number to process"),
  }),

  outputSchema: z.object({
    orderfulTransactionId: z
      .string()
      .describe("The Orderful transaction ID for the 856 ASN"),
    famousShipmentId: z
      .string()
      .uuid()
      .describe("The internal database ID of the Famous shipment record"),
    purchaseOrderNumber: z
      .string()
      .optional()
      .describe("The customer's purchase order number from the shipment"),
  }),

  configSchema: z.object({
    stream: z
      .enum(["TEST", "LIVE"])
      .default("TEST")
      .describe("Orderful stream to send to (TEST or LIVE)"),
    skipIfAlreadySent: z
      .boolean()
      .default(true)
      .describe(
        "Skip sending if the 856 has already been sent for this shipment",
      ),
    fetchFromFamous: z
      .boolean()
      .default(true)
      .describe(
        "Whether to fetch fresh data from Famous (if false, uses existing cached data)",
      ),
    skipIfShipDateOlderThanDays: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(
        "Skip sending EDI if the shipment's ship date is older than this many days (e.g., 7 to skip shipments older than a week)",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
      maximumInterval: "2 minutes",
    },
    // The retries above are for infrastructure — a network blip reaching
    // Orderful, a DB hiccup, a worker timeout. They must never re-run a send
    // whose failure is deterministic: every attempt re-runs the whole handler,
    // POST /transactions included, so a rejected ASN is simply transmitted
    // again. The handler decides which is which and rethrows deterministic
    // failures as this type. Keep in sync with
    // EDI_856_NON_RETRYABLE_ERROR_TYPE (pinned by a test).
    nonRetryableErrorTypes: ["Edi856NonRetryableError"],
  },
});
